/**
 * Invite Staff Member modal — extracted from StaffManagementPage so it can be
 * unit tested without mounting the ~2400-line parent page.
 *
 * Role picker is filtered to roles the caller may actually grant
 * (canGrant from @platform/shared-types) and never offers role_owner —
 * the server enforces the same rule (403) but the picker should not dangle
 * an option that will always be rejected.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Mail, Send, X } from 'lucide-react';
import SearchableDropdown from '../SearchableDropdown';
import { DatePicker } from '../DatePicker';
import { RubyGemMark } from '../RubyGemMark';
import { canGrant, OWNER_ROLE_ID, ADMIN_SECTIONS, type RolePermissions } from '@platform/shared-types';
import type { StaffRole } from '../../services/staffApi';

const INPUT =
  'w-full px-3.5 py-2.5 bg-white border border-stone-300 rounded-lg text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:border-admin-primary transition-colors disabled:opacity-60';

const SENSITIVE_SECTION_IDS = new Set(ADMIN_SECTIONS.filter((s) => s.sensitive).map((s) => s.id));
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface InviteStaffInput {
  email: string;
  name: string;
  roleId: string;
  title?: string;
  hireDate?: string;
}

export interface InviteStaffModalProps {
  open: boolean;
  onClose: () => void;
  roles: StaffRole[];
  myLevels: RolePermissions;
  isOwner: boolean;
  onInvite: (input: InviteStaffInput) => Promise<void>;
}

export default function InviteStaffModal({
  open, onClose, roles, myLevels, isOwner, onInvite,
}: InviteStaffModalProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [title, setTitle] = useState('');
  const [hireDate, setHireDate] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fresh form every time the modal opens.
  useEffect(() => {
    if (!open) return;
    setEmail(''); setName(''); setRoleId(''); setTitle(''); setHireDate('');
    setSending(false); setError(null);
  }, [open]);

  // Roles the caller may actually grant — role_owner is never offered (the
  // server 403s it anyway), and a non-owner only sees roles whose every
  // permission they themselves hold at an equal-or-higher level.
  const grantableRoles = useMemo(
    () => roles.filter((r) => r.id !== OWNER_ROLE_ID && canGrant(myLevels, r.permissions || {}, isOwner).ok),
    [roles, myLevels, isOwner],
  );

  const selectedRole = useMemo(
    () => grantableRoles.find((r) => r.id === roleId) || null,
    [grantableRoles, roleId],
  );

  const roleSummary = useMemo(() => {
    if (!selectedRole) return null;
    const perms = selectedRole.permissions || {};
    let edit = 0;
    let view = 0;
    let sensitive = false;
    for (const [sectionId, level] of Object.entries(perms)) {
      if (level === 'edit') edit += 1;
      else if (level === 'view') view += 1;
      if (SENSITIVE_SECTION_IDS.has(sectionId)) sensitive = true;
    }
    return { edit, view, sensitive };
  }, [selectedRole]);

  const trimmedEmail = email.trim().toLowerCase();
  const validEmail = EMAIL_RE.test(trimmedEmail);
  const trimmedName = name.trim();
  const canSend = !sending && !!roleId && validEmail && !!trimmedName;

  if (!open) return null;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      await onInvite({
        email: trimmedEmail,
        name: trimmedName,
        roleId,
        title: title.trim() || undefined,
        hireDate: hireDate || undefined,
      });
      // Success: the caller (StaffManagementPage) closes the modal and
      // refreshes the roster. Nothing more to do here.
    } catch (err: any) {
      setError(err?.message || 'Failed to invite staff member');
      setSending(false);
    }
  };

  const submitOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-stone-200 rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="bg-white text-stone-900 p-5 rounded-t-2xl border-b border-stone-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-admin-primary flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Invite Staff Member</h3>
                <p className="text-sm text-stone-600">Send a sign-in invitation</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              aria-label="Close"
              className="w-11 h-11 flex items-center justify-center hover:bg-stone-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {error && (
            <div role="alert" className="px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Email *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={submitOnEnter}
              className={INPUT}
              placeholder="person@rubytill.com"
              disabled={sending}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Full Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={submitOnEnter}
              className={INPUT}
              placeholder="e.g., Jordan Smith"
              disabled={sending}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Role *</label>
            <SearchableDropdown
              options={grantableRoles.map((r) => ({ value: r.id, label: r.name, subtitle: r.description || undefined }))}
              value={roleId || null}
              onChange={(val) => setRoleId(val || '')}
              placeholder="Choose a role…"
              disabled={sending}
            />
            {roleSummary && (
              <p className="mt-1.5 text-xs text-stone-500">
                {roleSummary.edit} Edit · {roleSummary.view} View
                {roleSummary.sensitive && <span className="ml-1.5">🛡 includes sensitive sections</span>}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Job Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={INPUT}
              placeholder="e.g., Support Engineer"
              disabled={sending}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Hire Date</label>
            <DatePicker value={hireDate} onChange={setHireDate} placeholder="Select hire date" />
          </div>
          <p className="text-xs text-stone-500">
            We'll email them a sign-in link and a temporary password. They'll set a password and an authenticator app on first sign-in.
          </p>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-stone-200">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="px-5 py-2.5 bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            className="px-5 py-2.5 bg-admin-primary hover:bg-admin-secondary text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 transition-colors"
          >
            {sending ? <RubyGemMark size={16} drawing color="currentColor" /> : <Send size={16} />}
            Send Invite
          </button>
        </div>
      </div>
    </div>
  );
}
